import {
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    signal,
    Signal,
    viewChild,
} from '@angular/core';
import { CustomTranslatePipe } from '../../../shared/pipes/custom-translate.pipe';
import { FileProgressDialogComponent } from '../deposit-files';
import { DepositFormDataService } from '../../deposit-form-data.service';
import { FileUploadService } from '../../../parts/handle-files/file-upload/file-upload.service';
import { GrobidService } from '../../../parts/handle-files/file-upload/grobid.service';
import { DepositFormService } from '../../deposit-form.service';
import { DepositSearchForService } from '../deposit-search-for';
import {
    DisplayMode,
    FileUtils,
    MediaService,
    SvgViewboxDirective,
    objEqualShallow,
} from '@exl-ng/mulo-core';
import { MatDialog } from '@angular/material/dialog';
import {
    FieldType,
    FieldTypeEnum,
} from '../../../esp-dynamic-form/dynamic-form-util';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { PDFMetadata } from '../../../shared/interfaces/pdf-metadata.interface';
import { EsploroRecord } from '../../../shared/interfaces/esploro-record.model';
import { FilesFromOnlineDialogComponent } from '../../../parts/dialogs/files-from-online-dialog/files-from-online-dialog.component';
import { Link } from '../../../shared/interfaces/link.model';
import { DepositForm } from '../../../shared/configurations/deposit-form.constant';
import {
    ActionListItemActionComponent,
    ContextFrameComponent,
    ExtendedFile,
    FileActionDirective,
    FileDropComponent,
    FileListComponent,
    FileListItemComponent,
    FileSizePipe,
    FileTypeIconPipe,
    GrowInAnimation,
    GrowOutAnimation,
    HeightInAnimation,
    HeightOutAnimation,
} from '@exl-ng/mulo-common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
    FormArray,
    FormGroup,
    FormGroupDirective,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT, TitleCasePipe } from '@angular/common';
import { EntityService } from '../../../core/entity.service';
import { EsploroFile } from '../../../shared/interfaces/file.model';
import { ResearchPortalFileDataModel } from '../../../shared/interfaces/research-portal-file-data-model.interface';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatInputModule } from '@angular/material/input';
import { ConfigurationHandlerService } from '../../../core/configuration-handler.service';
import { AccessRightsComponent } from '../../../parts/access-rights/access-rights.component';
import { Moment } from 'moment';
import { GroupByPipe } from '../../../shared/pipes/group-by.pipe';
import { MatTooltip } from '@angular/material/tooltip';

type FileModel<T = any> = T extends ResearchPortalFileDataModel ? T : never;

@Component({
    selector: 'esp-deposit-files-links',
    templateUrl: './deposit-files-links.component.html',
    styleUrl: './deposit-files-links.component.scss',
    animations: [
        GrowInAnimation,
        GrowOutAnimation,
        HeightInAnimation,
        HeightOutAnimation,
    ],
    imports: [
        ActionListItemActionComponent,
        ContextFrameComponent,
        CustomTranslatePipe,
        FileActionDirective,
        FileDropComponent,
        FileListComponent,
        FileListItemComponent,
        FileSizePipe,
        FileTypeIconPipe,
        FormsModule,
        MatButton,
        MatFormFieldModule,
        MatIcon,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        SvgViewboxDirective,
        TitleCasePipe,
        TranslateModule,
        AccessRightsComponent,
        GroupByPipe,
        MatTooltip,
    ],
})
export class DepositFilesLinksComponent implements OnInit {
    dataSvc = inject(DepositFormDataService);
    private fileUploadService = inject(FileUploadService);
    private grobidService = inject(GrobidService);
    private formSvc = inject(DepositFormService);
    private depositSearchForService = inject(DepositSearchForService);
    media = inject(MediaService);
    matDialog = inject(MatDialog);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private formBuilder = inject(UntypedFormBuilder);
    private document = inject<Document>(DOCUMENT);
    private _window = this.document.defaultView;
    private configSvc = inject(ConfigurationHandlerService);

    readonly isAutoPopulateEnabled = input(true);
    readonly multiple = input(false);
    readonly jump = input<any>(undefined);
    readonly displayAccessRights = input(true);
    readonly displayLicense = input(true);
    readonly displayContentType = input(true);
    readonly displayDescription = input(true);
    readonly sectionOrder = input<FieldType[]>([
        FieldTypeEnum.Files,
        FieldTypeEnum.Links,
    ]);
    protected readonly fieldType = FieldTypeEnum;

    readonly alwaysExpandPanel = input(false);
    readonly editableFiles = input(true);
    miniFileUploaderOpen = false;
    fileUploadRegex = new RegExp('/esploro/(.+)/');
    filesForm: UntypedFormGroup;
    linksForm: UntypedFormGroup;

    readonly parentId = input<string>(undefined);
    readonly openURLLink = input<string>(undefined);
    readonly entityService = input<EntityService>(undefined);

    readonly displayDateFormat = input<string>(undefined);

    readonly fileTypes: Signal<string[]>;
    readonly linkTypes: Signal<string[]>;
    readonly licenseTypes: Signal<any[]>;

    readonly fileContextFrameElem = viewChild('fileContextFrame', {
        read: ElementRef,
    });
    readonly linkContextFrameElem = viewChild('linkContextFrame', {
        read: ElementRef,
    });
    readonly fileFormDirective = viewChild('fileForm', {
        read: FormGroupDirective,
    });
    readonly linkFormDirective = viewChild('linkForm', {
        read: FormGroupDirective,
    });

    destroyRef = inject(DestroyRef);

    currentFile = signal<EsploroFile>(undefined, { equal: objEqualShallow });
    replacingFile = signal<EsploroFile>(undefined, { equal: objEqualShallow });
    closeFilesForm = signal(true);
    closeLinksForm = signal(true);
    galleryFileSizeError: boolean;
    fileSizeError: boolean;
    hasSubmitError = false;
    submitInProgress = signal(false);

    readonly fileEditIndex = signal(-1);
    readonly filesCtrl = input<FormArray>(this.dataSvc.files);
    files: Signal<any[]>;
    readonly fileInEdit = computed(
        () => this.filesCtrl()?.at(this.fileEditIndex()) as FormGroup,
    );
    readonly filesFormMode = signal<DisplayMode>(undefined);

    readonly linkEditIndex = signal(-1);
    readonly linksCtrl = input<FormArray>(this.dataSvc.links);
    links: Signal<any[]>;
    readonly linkInEdit = computed(
        () => this.linksCtrl()?.at(this.linkEditIndex()) as FormGroup,
    );
    readonly linksFormMode = signal<DisplayMode>(undefined);

    constructor() {
        this.fileTypes = computed(() => {
            const assetFileAndLinkTypesMT =
                this.configSvc.config().mappingTables?.[
                    'AssetFileAndLinkTypes'
                ];
            return assetFileAndLinkTypesMT
                .filter(
                    (row) =>
                        row.enabled === 'true' &&
                        (row.source1 === 'file' || row.source1 === 'both') &&
                        (row.source2.includes('all') ||
                            row.source2.includes(this.dataSvc.category.value)),
                )
                .map((row) => row.target);
        });
        this.linkTypes = computed(() => {
            const assetFileAndLinkTypesMT =
                this.configSvc.config().mappingTables?.[
                    'AssetFileAndLinkTypes'
                ];
            return assetFileAndLinkTypesMT
                .filter(
                    (row) =>
                        row.enabled === 'true' &&
                        (row.source1 === 'link' || row.source1 === 'both') &&
                        (row.source2.includes('all') ||
                            row.source2.includes(this.dataSvc.category.value)),
                )
                .map((row) => row.target);
        });
        this.licenseTypes = computed(() => {
            const licenseTypesMT =
                this.configSvc.config().mappingTables?.['EsploroLicensesData'];

            return licenseTypesMT
                .filter(
                    (row) =>
                        row.enabled === 'true' &&
                        row.source5 === 'true' &&
                        (row.source2.includes('all') ||
                            row.source2.includes(this.dataSvc.category.value)),
                )
                .map((row) => ({
                    title: row.source1,
                    category: row.source2,
                    link: row.source3,
                    forResearcherView: row.source5,
                }));
        });
        this.files = toSignal(this.filesCtrl()?.valueChanges, {
            initialValue: this.filesCtrl().value,
        });
        this.links = toSignal(this.linksCtrl()?.valueChanges, {
            initialValue: this.linksCtrl().value,
        });
    }
    ngOnInit() {
        this.filesForm = this.formBuilder.group({
            contentType: [''],
            access: [''],
            description: [''],
            index: [null],
            license: [''],
            accessRightsReason: [''],
            accessRightsMoment: [''],
        });
        this.linksForm = this.formBuilder.group({
            url: ['', Validators.required],
            type: [''],
            license: [''],
            description: [''],
            sortOrder: [null],
        });
        this.updateAmazonDetails();
    }

    addNewFile(files: ExtendedFile[]) {
        for (const file of files as EsploroFile[]) {
            if (!this.dataSvc.isFileExists(file)) {
                file.uploadProgress = 0;
                this.uploadFileToAmazon(file, true);
                this.callGrobid(file);
                if (!this.miniFileUploaderOpen) {
                    this.showUploadMiniDialog();
                }
            }
        }
    }

    showUploadMiniDialog() {
        this.miniFileUploaderOpen = true;
        const dialogRef = this.matDialog.open(FileProgressDialogComponent, {
            panelClass: 'exl-file-progress-dialog-overlay-pane',
            hasBackdrop: false,
            disableClose: true,
            position: {
                bottom: '16px',
                left: '16px',
            },
            data: { files: this.dataSvc.files.value },
        });

        dialogRef.afterOpened().subscribe((val) => {});
        dialogRef.afterClosed().subscribe((result) => {
            this.miniFileUploaderOpen = false;
        });
    }

    uploadFileToAmazon(file, updateDataSvc = false) {
        if (updateDataSvc) {
            this.dataSvc.addToFiles(file);
        }
        file.subscriber = this.fileUploadService
            .uploadS3(file)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (event) => {
                    if (event.type === HttpEventType.UploadProgress) {
                        const percentDone = Math.round(
                            (100 * event.loaded) / event.total,
                        );
                        if (updateDataSvc) {
                            this.dataSvc.setFileUploaded(file, percentDone);
                        } else {
                            this.filesForm.enable();
                            this.setCurrentFile(file);
                            this.fileSizeValidator();
                        }
                    } else if (event instanceof HttpResponse) {
                    }
                },
                error: (error) => {
                    console.error('error uploading to amazon', error);
                },
            });
    }

    callGrobid(file) {
        if (
            this.dataSvc.isReservedOrRegisteredDoi() ||
            !this.isAutoPopulateEnabled()
        ) {
            return;
        }
        const fileSizeMB = file.size / 1024 / 1024;
        if (
            !this.dataSvc.enrichForm &&
            !this.grobidService.grobidExecuted &&
            file.extension === 'pdf' &&
            fileSizeMB < 5
        ) {
            this.grobidService.grobidExecuted = true;
            this.grobidService
                .grobid(file)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(
                    (data) => {
                        const pdsMetadata = data as PDFMetadata;
                        this.enrichForm(pdsMetadata);
                    },
                    (error) => {
                        this.grobidService.grobidExecuted = false;
                    },
                );
        }
    }

    enrichForm(pdfMetadata) {
        if (pdfMetadata.doi) {
            this.enrichFromDoi(pdfMetadata);
        } else {
            this.enrichFromPDF(pdfMetadata);
        }
    }

    enrichFromDoi(pdfMetadata) {
        this.depositSearchForService
            .enrichFromFileWithDoi(this.dataSvc.type.value, pdfMetadata.doi)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                (data) => {
                    if (data && !this.isEmptyObject(data)) {
                        const esploroRecord = data as EsploroRecord;
                        this.dataSvc.populateForm(esploroRecord, true);
                    } else {
                        this.enrichFromPDF(pdfMetadata);
                    }
                },
                (error) => {
                    this.enrichFromPDF(pdfMetadata);
                },
            );
    }

    enrichFromPDF(pdfMetadata: PDFMetadata) {
        if (pdfMetadata && !this.isEmptyObject(pdfMetadata)) {
            this.dataSvc.populateFormFromPDF(pdfMetadata);
        }
    }

    isEmptyObject(o) {
        return Object.keys(o).every(function (x) {
            return o[x].length === 0;
        });
    }

    onAddLink() {
        this.showUrlDialog();
    }

    showUrlDialog(link?, index?: number) {
        const dialogRef = this.matDialog.open(FilesFromOnlineDialogComponent, {
            minWidth: this.media.is('lt-md') ? 'auto' : '500px',
            data: {
                index,
                title: link
                    ? 'research.links.title.edit'
                    : 'research.links.title.add',
                url: link ? link.url : '',
                description: link ? link.description : '',
                new: !link,
                actions: {
                    cancel: 'research.links.no',
                    confirm: link
                        ? 'research.links.yes.save'
                        : 'research.links.yes.add',
                },
            },
        });
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                if (result && result !== 'cancel') {
                    if (result.index >= 0) {
                        this.onLinkEdited(result);
                    } else {
                        this.onNewLinkAdded(result);
                    }
                }
            });
    }

    onNewLinkAdded(link) {
        const newLink = new Link(link.url, link.description, link.type);
        this.dataSvc.updateLinks(newLink);
    }

    onLinkEdited(link) {
        this.dataSvc.updateLink(link.index, link);
    }

    deleteFileFromUpload(fileName) {
        this.fileUploadService
            .deleteFileFromUpload(
                DepositForm.UPLOAD_FILE_PATH + this.dataSvc.filePath.value,
                fileName,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    handleFileDeletion(file) {
        if (file.mid) {
            //file in storage
            this.dataSvc.addDeletedStorageFilesNames(file);
        } else {
            //file in upload
            this.deleteFileFromUpload(file.name);
        }
    }

    handleViewAction(item, index) {
        if (item.viewerLink) {
            this.router.navigate(['.'], {
                relativeTo: this.route,
                queryParamsHandling: 'preserve',
                fragment: 'file-' + index,
                replaceUrl: true,
            });
        } else {
            const url = item.url;
            this._window.open(url, '_blank');
        }
    }

    handleEditFileAction(index) {
        this.setLinksForm(null);
        this.setFilesForm(index, this.files()[index]);
        const fileContextFrameElem = this.fileContextFrameElem();
        fileContextFrameElem.nativeElement.tabIndex = 0;
        fileContextFrameElem.nativeElement.focus();
        fileContextFrameElem.nativeElement.tabIndex = -1;
    }

    handleEditLinkAction(index) {
        this.setFilesForm(null);
        this.setLinksForm(index, this.linksCtrl().at(index).value);
        const linkContextFrameElem = this.linkContextFrameElem();
        linkContextFrameElem.nativeElement.tabIndex = 0;
        linkContextFrameElem.nativeElement.focus();
        linkContextFrameElem.nativeElement.tabIndex = -1;
    }

    handleFileSubmit(andClose = true) {
        this.submitInProgress.set(true);
        this.closeFilesForm.set(andClose);
        if (this.filesFormMode() === 'add') {
            this.addFile();
        } else {
            this.editFile();
        }
    }

    handleLinkSubmit(andClose = true) {
        this.submitInProgress.set(true);
        this.closeLinksForm.set(andClose);
        if (this.linksFormMode() === 'add') {
            this.addLink();
        } else {
            this.editLink();
        }
    }

    handleFileFormOpen() {
        this.closeLinksForm.set(true);
        this.setLinksForm(null);
        this.filesFormMode.update((prev) => prev || 'add');
        if (this.filesFormMode() === 'add') {
            this.filesForm.disable();
        }
    }

    handleLinkFormOpen() {
        this.closeFilesForm.set(true);
        this.setFilesForm(null);
        this.linksFormMode.update((prev) => prev || 'add');
    }

    handleFileFormClose() {
        this.setFilesForm(null);
        this.filesFormMode.set(null);
        this.submitInProgress.set(false);
        this.currentFile.set(null);
    }

    handleLinkFormClose() {
        this.setLinksForm(null);
        this.linksFormMode.set(null);
    }

    handleAddAnotherFile(event: MouseEvent) {
        event.preventDefault();
        this.handleFileSubmit(false);
    }

    handleAddAnotherLink(event: MouseEvent) {
        event.preventDefault();
        this.handleLinkSubmit(false);
    }

    setFilesForm(index, fileData?: EsploroFile) {
        if (index === undefined || index === null) {
            this.fileEditIndex.set(-1);
            this.galleryFileSizeError = false;
            this.fileSizeError = false;
            this.currentFile.set(null);
            this.filesForm.reset();
            this.filesForm.disable();
            if (this.closeFilesForm() === false) {
                this.filesFormMode.set('add');
            } else {
                this.filesFormMode.set(null);
                this.closeFilesForm.set(true);
            }
            if (this.fileFormDirective()?.form) {
                this.fileFormDirective().resetForm();
            }
        } else {
            this.filesFormMode.set('edit');
            this.fileEditIndex.set(index);
            this.currentFile.set(fileData);
            this.filesForm.enable();
            this.filesForm.patchValue(fileData);
        }
        this.submitInProgress.set(false);
    }

    setLinksForm(index, linkData: any = {}) {
        if (index === undefined || index === null) {
            this.linkEditIndex.set(-1);
            this.linksForm?.reset();
            if (this.closeLinksForm() === false) {
                this.linksFormMode.set('add');
            } else {
                this.linksFormMode.set(null);
                this.closeLinksForm.set(true);
            }
            if (this.linkFormDirective()?.form) {
                this.linkFormDirective().resetForm();
            }
        } else {
            this.linksFormMode.set('edit');
            this.linkEditIndex.set(index);
            this.linksForm.patchValue(linkData);
        }
        this.submitInProgress.set(false);
    }

    addFile() {
        const index = this.filesCtrl()?.value.reduce(
            (p, c) => Math.max(p, c.index + 1),
            0,
        );
        if (this.filesForm?.get('index').value === null) {
            this.filesForm.patchValue({ index });
        }
        const arMoment: Moment = this.filesForm.get('accessRightsMoment').value;
        this.currentFile.update((prev) =>
            Object.assign(prev, this.filesForm.value, {
                accessRightsFixedDate: arMoment?.toDate(),
                accessRightsFixedYear: arMoment?.year(),
                accessRightsFixedMonth: arMoment?.month(),
                accessRightsFixedDay: arMoment?.day(),
            }),
        );
        this.dataSvc.addToFiles(this.currentFile());
        this.setFilesForm(null);
    }

    editFile() {
        const file = {
            ...this.currentFile(),
            ...this.filesForm.value,
        };
        if (this.replacingFile()) {
            this.replacingFile.set(null);
        }
        const fileGroup = this.fileInEdit();
        fileGroup.patchValue(file);
        this.setFilesForm(null);
    }

    removeFile(index: number) {
        this.dataSvc.removeFile(index);
        this.setFilesForm(null);
    }

    addLink() {
        const sortOrder = this.links()?.reduce(
            (p, c) => Math.max(p, c.sortOrder + 1),
            0,
        );
        if (this.linksForm?.get('sortOrder').value === null) {
            this.linksForm.patchValue({ sortOrder });
        }
        this.dataSvc.updateLinks(this.linksForm.value);
        this.setLinksForm(null);
    }

    editLink() {
        const linkGroup = this.linkInEdit();
        linkGroup.patchValue({ ...this.linksForm.value });
        this.setLinksForm(null);
    }

    removeLink(index: number) {
        this.dataSvc.removeLink(index);
        this.setLinksForm(null);
    }

    handleFileDrop(files: EsploroFile[]) {
        const file = files[0];

        if (!this.isFileExists(file)) {
            // completely new file
            FileUtils.sanitizeSVG(file).then(
                (cleanOrNull) => {
                    if (cleanOrNull !== null) {
                        if (this.currentFile() !== null) {
                            this.replacingFile.set(this.currentFile());
                            this.currentFile.set(null);
                        } else {
                            this.replacingFile.set(null);
                        }
                        this.hasSubmitError = false;
                        this.uploadFileToAmazon(file);
                    } else {
                        this.hasSubmitError = true;
                        console.error('sanitization failed');
                    }
                },
                (rejectReason) => {
                    this.hasSubmitError = true;
                    console.error('sanitization failed: ', rejectReason);
                },
            );
        } else if (
            this.getExistingFileIndex(file) === this.currentFile().index
        ) {
            // switched to a different file and now going back
            this.currentFile.set(null);
            setTimeout(() => this.setCurrentFile(file));
        }
    }

    setCurrentFile(file: EsploroFile) {
        file.index = this.fileEditIndex();
        this.currentFile.set(file);
    }

    isSameFile = (file: ExtendedFile, newFile: ExtendedFile) =>
        newFile.name === file?.name && newFile.size === file?.size;

    getExistingFileIndex = (searchFile: ExtendedFile) =>
        this.files()?.findIndex((_) => this.isSameFile(_, searchFile));

    isFileExists = (newFile: ExtendedFile) =>
        this.getExistingFileIndex(newFile) > -1;

    fileSizeValidator() {
        if (!this.currentFile()) {
            this.fileSizeError = false;
            return;
        }
        const size = Number(this.currentFile().size) / 1024 ** 2;
        this.fileSizeError = size > 500;
    }

    updateAmazonDetails() {
        this.formSvc
            .getAmazonInfo()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((amazonInfo) => {
                const match = this.fileUploadRegex.exec(amazonInfo.uploadPath);
                if (match) {
                    this.dataSvc.updateFilePath(match[1]);
                }
            });
    }
}
